import {
  dateToISO,
  DefectLog,
  FoodDrawal,
  FoodPoisoning,
} from "@enos-monorepo/lib";
import { isSameDay } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { DefectLogsInfoModal, DrawalInfoModal } from "../Modals/LogModals";
import { PoisoningInfoModal } from "../Modals/LogModals/PoisiningInfoModal";
import { LogInfo } from "./LogInfo";

interface ReportTableProps {
  title: string;
  selectedMonth?: Date;
  companyId: string;
  selectedLocationId: string;
  report: {
    drawals: FoodDrawal[];
    poisonings: FoodPoisoning[];
    defects: DefectLog[];
  };
}

export function LogTable({
  title,
  selectedMonth,
  report,
  companyId,
  selectedLocationId,
}: ReportTableProps) {
  const { t } = useTranslation();
  const [selectedDefectLogs, setSelectedDefectLogs] = useState<DefectLog[]>([]);
  const [selectedPoisinings, setSelectedPoisinings] = useState<FoodPoisoning[]>(
    [],
  );
  const [selectedDrawals, setSelectedDrawals] = useState<FoodDrawal[]>([]);

  const [isDefectModalOpen, setIsDefectModalOpen] = useState(false);
  const [isPoisoningModalOpen, setIsPoisoningModalOpen] = useState(false);
  const [isDrawalModalOpen, setIsDrawalModalOpen] = useState(false);

  const handleDefectLogModal = (defects: DefectLog[]) => {
    setSelectedDefectLogs(defects);
    setIsDefectModalOpen(true);
  };

  const handlePoisoningModal = (poisoning: FoodPoisoning[]) => {
    setSelectedPoisinings(poisoning);
    setIsPoisoningModalOpen(true);
  };

  const handleDrawalModal = (drawal: FoodDrawal[]) => {
    setSelectedDrawals(drawal);
    setIsDrawalModalOpen(true);
  };

  const getDatesInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    return Array.from({ length: daysInMonth }, (_, i) => {
      return new Date(year, month, i + 1);
    });
  };

  const daysInMonth = selectedMonth
    ? getDatesInMonth(selectedMonth)
    : getDatesInMonth(new Date());

  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  return (
    <div className="grid">
      <div className="card min-w-full">
        <div className="card-table scrollable-x-auto">
          <table className="table align-middle table-auto table-border table-xs ">
            <thead>
              <tr>
                <th style={{ width: 200, paddingTop: 23, paddingBottom: 23 }}>
                  {title}
                </th>
                {daysInMonth.map((day) => (
                  <th
                    key={day.toString()}
                    style={{
                      textAlign: "center",
                      backgroundColor: isWeekend(day) ? "#EFF6FF" : undefined,
                      padding: 0,
                    }}
                  >
                    {day.getDate()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t("food.Fail Report")}</td>
                {daysInMonth.map((day, index) => {
                  const filteredDefects = report.defects.filter((d) =>
                    isSameDay(dateToISO(day), d.creationDate),
                  );
                  return (
                    <td
                      key={"failReport" + index}
                      style={{
                        backgroundColor: isWeekend(day) ? "#EFF6FF" : undefined,
                        paddingRight: 0,
                      }}
                      className="!py-2.5 !px-1.5 !w-0 "
                    >
                      <div className="flex justify-center">
                        <LogInfo
                          onClick={() => handleDefectLogModal(filteredDefects)}
                          hasData={filteredDefects.length > 0}
                        >
                          {filteredDefects.length}
                        </LogInfo>
                      </div>
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td>{t("food.Food Poisoning")}</td>
                {daysInMonth.map((day, index) => {
                  const filteredPoisining = report.poisonings.filter((p) =>
                    isSameDay(dateToISO(day), p.creationDate),
                  );
                  return (
                    <td
                      key={"foodPoisoning" + index}
                      style={{
                        backgroundColor: isWeekend(day) ? "#EFF6FF" : undefined,
                        paddingRight: 0,
                      }}
                      className="!py-2.5 !px-1.5 !w-0 "
                    >
                      <div className="flex justify-center">
                        <LogInfo
                          onClick={() =>
                            handlePoisoningModal(filteredPoisining)
                          }
                          hasData={filteredPoisining.length > 0}
                        >
                          {filteredPoisining.length}
                        </LogInfo>
                      </div>
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td>{t("food.Product Return")}</td>
                {daysInMonth.map((day, index) => {
                  const filteredDrawals = report.drawals.filter((d) =>
                    isSameDay(dateToISO(day), d.creationDate),
                  );
                  return (
                    <td
                      key={"productReturn" + index}
                      style={{
                        backgroundColor: isWeekend(day) ? "#EFF6FF" : undefined,
                        paddingRight: 0,
                      }}
                      className="!py-2.5 !px-1.5 !w-0 "
                    >
                      <div className="flex justify-center">
                        <LogInfo
                          onClick={() => handleDrawalModal(filteredDrawals)}
                          hasData={filteredDrawals.length > 0}
                        >
                          {filteredDrawals.length}
                        </LogInfo>
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <DefectLogsInfoModal
        companyId={companyId}
        selectedLocationId={selectedLocationId}
        selectedDefectLogs={selectedDefectLogs}
        open={isDefectModalOpen}
        onClose={() => setIsDefectModalOpen(false)}
      />
      <PoisoningInfoModal
        companyId={companyId}
        selectedLocationId={selectedLocationId}
        selectedPoisonings={selectedPoisinings}
        open={isPoisoningModalOpen}
        onClose={() => setIsPoisoningModalOpen(false)}
      />
      <DrawalInfoModal
        companyId={companyId}
        selectedLocationId={selectedLocationId}
        selectedDrawals={selectedDrawals}
        open={isDrawalModalOpen}
        onClose={() => setIsDrawalModalOpen(false)}
      />
    </div>
  );
}
